/**
 * OnlineEntryForm.jsx
 * Online version of the ACU C8-RR entry form — replaces the paper process
 * for events from the 2027 season onward. Submit/Print POST to
 * /api/entries/submit, which creates a 'pending' row in entry_submissions
 * for the secretary to review — see memory/project_online_entry_form.md and
 * memory/project_online_entry_submission_pipeline.md for the background.
 *
 * Route: /enter/:calendarId
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import '../Secretary.css';
import '../EntryForm.css';

const API_KEY = import.meta.env.VITE_API_KEY || '';

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const DAY_ABBREVS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dayAbbrev = (iso) => {
  if (!iso) return '';
  return DAY_ABBREVS[new Date(`${iso}T00:00:00`).getDay()];
};

// Reference is never typed by the entrant — computed so the secretary can match
// a bank statement line back to a rider/event without relying on free text.
// Day licence / Give It A Go riders get a marked reference instead, so the
// financial secretary can tell those payments apart on the bank statement:
// the prefix plus as many surname letters as fit UK banks' 18-character limit,
// e.g. GIAG-QUIL-0001-Sat, DL-QUILLF-0001-Sat.
const LICENCE_REF_PREFIX = { giag: 'GIAG', day_licence: 'DL' };
const BANK_REF_MAX = 18;

const computePaymentReference = (acuLicence, event, surname, licenceOption) => {
  const permitDigits = (event?.acu_permit || '').replace(/\D/g, '');
  const last4 = permitDigits.slice(-4);
  const day = dayAbbrev(event?.date);
  const prefix = LICENCE_REF_PREFIX[licenceOption];
  if (prefix) {
    const letters = (surname || '').replace(/[^A-Za-z]/g, '').toUpperCase();
    if (!letters) return '';
    const room = BANK_REF_MAX - [prefix, last4, day].filter(Boolean).join('-').length - 1;
    return [prefix, letters.slice(0, room), last4, day].filter(Boolean).join('-');
  }
  // Strip ALL whitespace, not just leading/trailing — a stray internal space
  // (e.g. "AC 123456" instead of "AC123456") would otherwise flow straight
  // into the computed reference, breaking exact-string matching between
  // riders who are meant to share one BACS reference (coverage groups).
  const acu = (acuLicence || '').replace(/\s+/g, '');
  // Never show a reference at all — not even a partial one — before the rider's
  // ACU Licence number is entered. Skipping straight to the payment step would
  // otherwise silently produce a reference missing its rider-identifying segment.
  if (!acu) return '';
  return [acu, last4, day].filter(Boolean).join('-');
};

const todayIso = () => new Date().toISOString().slice(0, 10);

// Online entry only replaces the paper C8-RR process from the 2027 season
// onward — matches ONLINE_ENTRY_FIRST_YEAR in CalendarEditor.jsx.
const ONLINE_ENTRY_FIRST_YEAR = 2027;

// Same 7-field readiness gate CalendarEditor.jsx already uses to decide
// whether the "Entry List" link is shown to the secretary
// (ENTRY_LIST_REQUIRED_FIELDS/missingEntryListFields()) — kept as an
// independent copy here since this component reads the /header endpoint's
// shape, not the calendar-editor's raw event row. Update both together if
// the requirement list ever changes. POST /api/entries/submit re-checks the
// same 7 fields server-side, so this is a UX nicety (fail fast, name what's
// missing) rather than the actual enforcement point.
const ENTRY_READINESS_FIELDS = [
  { key: 'acu_permit', label: 'Permit No.' },
  { key: 'course_licence_no', label: 'Course Lic/Cert No.' },
  { key: 'first_bike_price', label: 'First Bike entry fee' },
  { key: 'first_3w_price', label: 'First 3-Wheeler entry fee' },
  { key: 'regs', label: 'Regulations (URL or upload)' },
  { key: 'has_secretary_email', label: 'Main Secretary email' },
  { key: 'has_finance_sec_email', label: 'Finance Secretary email' },
];

const isBlank = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

function missingReadinessFields(event) {
  return ENTRY_READINESS_FIELDS.filter(({ key }) => {
    if (key === 'regs') return !(event.has_regs_pdf || !isBlank(event.regs_url));
    if (key === 'first_bike_price') return event.prices?.first_bike == null;
    if (key === 'first_3w_price') return event.prices?.first_3w == null;
    if (key === 'has_secretary_email' || key === 'has_finance_sec_email') return !event[key];
    return isBlank(event[key]);
  }).concat(event.giag && event.prices?.giag == null ? [{ key: 'giag_price', label: 'Give It A Go price' }] : []);
}

const DECLARATION_VERSION = 'v2';

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) throw new Error((json && json.error) || 'Submission failed');
  return json;
}

const pad2 = (n) => String(n).padStart(2, '0');

const MONTHS = [
  { value: '01', label: 'January' }, { value: '02', label: 'February' }, { value: '03', label: 'March' },
  { value: '04', label: 'April' },   { value: '05', label: 'May' },      { value: '06', label: 'June' },
  { value: '07', label: 'July' },    { value: '08', label: 'August' },   { value: '09', label: 'September' },
  { value: '10', label: 'October' }, { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const CURRENT_YEAR = new Date().getFullYear();
const DOB_YEARS = Array.from({ length: 101 }, (_, i) => String(CURRENT_YEAR - i));

const daysInMonth = (year, month) => (year && month ? new Date(Number(year), Number(month), 0).getDate() : 31);

// Common UK/webmail domains — used to catch typo'd email domains (eg "gmial.com") without
// requiring a verification link or a second "confirm email" field.
const COMMON_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.co.uk', 'outlook.com', 'outlook.co.uk',
  'live.co.uk', 'live.com', 'yahoo.com', 'yahoo.co.uk', 'icloud.com', 'btinternet.com', 'sky.com',
  'virginmedia.com', 'talktalk.net', 'aol.com', 'me.com', 'msn.com',
];

const levenshtein = (a, b) => {
  const dp = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) dp[i][0] = i;
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[a.length][b.length];
};

// Returns a corrected email if the domain is a close (but not exact) match to a common
// provider, eg "bob@gmial.com" -> "bob@gmail.com". Returns null when there's nothing to suggest.
const suggestEmailDomain = (email) => {
  const at = email.lastIndexOf('@');
  if (at < 0) return null;
  const domain = email.slice(at + 1).trim().toLowerCase();
  if (!domain || COMMON_EMAIL_DOMAINS.includes(domain)) return null;
  let best = null;
  let bestDist = Infinity;
  for (const known of COMMON_EMAIL_DOMAINS) {
    const dist = levenshtein(domain, known);
    if (dist < bestDist) { bestDist = dist; best = known; }
  }
  return best && bestDist <= 2 ? email.slice(0, at + 1) + best : null;
};

// Split/rebuild date-of-birth as three <select>s (Day/Month/Year) instead of a native
// date input — riders skew older, and scrolling a calendar widget back 60+ years is slow.
//
// Keeps its own y/m/d selection state rather than deriving it purely from `value`: until
// all three are picked, the composed ISO date is '' — deriving from that would make a
// lone selection (eg picking the year first) immediately snap back to blank.
function DobSelect({ value, onChange, onBlur }) {
  const initial = value ? value.split('-') : ['', '', ''];
  const [year, setYear] = useState(initial[0] || '');
  const [month, setMonth] = useState(initial[1] || '');
  const [day, setDay] = useState(initial[2] || '');
  const dayOptions = Array.from({ length: daysInMonth(year, month) }, (_, i) => pad2(i + 1));

  const commit = (ny, nm, nd) => {
    setYear(ny); setMonth(nm); setDay(nd);
    if (!ny || !nm || !nd) { onChange(''); return; }
    const clampedDay = pad2(Math.min(Number(nd), daysInMonth(ny, nm)));
    onChange(`${ny}-${nm}-${clampedDay}`);
  };

  return (
    <div className="form-row-3 dob-select">
      <select aria-label="Day" value={day} onChange={e => commit(year, month, e.target.value)} onBlur={onBlur}>
        <option value="">Day</option>
        {dayOptions.map(n => <option key={n} value={n}>{Number(n)}</option>)}
      </select>
      <select aria-label="Month" value={month} onChange={e => commit(year, e.target.value, day)} onBlur={onBlur}>
        <option value="">Month</option>
        {MONTHS.map(mo => <option key={mo.value} value={mo.value}>{mo.label}</option>)}
      </select>
      <select aria-label="Year" value={year} onChange={e => commit(e.target.value, month, day)} onBlur={onBlur}>
        <option value="">Year</option>
        {DOB_YEARS.map(yr => <option key={yr} value={yr}>{yr}</option>)}
      </select>
    </div>
  );
}

// Shown inline, right under a field, once it's been "touched" (blurred at
// least once) or a submit was attempted — see the touched/submitAttempted
// state in OnlineEntryForm itself.
function FieldError({ show, message }) {
  if (!show || !message) return null;
  return <p className="field-error">⚠ {message}</p>;
}

const ageOnDate = (dobIso, onIso) => {
  if (!dobIso) return null;
  const dob = new Date(dobIso);
  const on = new Date(onIso || todayIso());
  let age = on.getFullYear() - dob.getFullYear();
  const m = on.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && on.getDate() < dob.getDate())) age--;
  return age;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (v) => EMAIL_RE.test(v.trim());
// Digits only, no spaces/dashes/letters — a UK-specific postcode format
// check was too strict (entrants can be from outside the UK), so postcode
// is free text with no format validation at all. Phone and ACU licence are
// both meant to be plain digit strings, so validate them as such directly
// rather than stripping non-digits first and only checking the count.
// Digits with the usual separators (spaces, dashes, brackets) and an
// optional leading +, e.g. "+44 (0)7700 900123" or "01234-567890"; 10-15
// digits in all, so a typo with a missing or extra digit still shows.
const isValidPhone = (v) => {
  const t = v.trim();
  if (!/^\+?[\d\s()-]+$/.test(t)) return false;
  const digits = t.replace(/\D/g, '').length;
  return digits >= 10 && digits <= 15;
};
const isValidAcuLicence = (v) => /^\d+$/.test(v.trim());
const isValidDigits = (v) => /^\d+$/.test(v.trim());
const isValidDecimal = (v) => /^\d+(\.\d+)?$/.test(v.trim());

const CLASS_FLAGS = [
  { key: 'openCapacity', label: 'Open Capacity' },
  { key: 'forgottenEra', label: 'Forgotten Era' },
  { key: 'roadLegal',    label: 'Road Legal' },
  { key: 'vintage',      label: 'Vintage' },
  { key: 'classic',      label: 'Classic' },
];

const VEHICLE_TYPES = [
  { value: 'Motorcycle', label: 'Motorcycle' },
  { value: 'Sidecar',    label: 'Sidecar' },
  { value: 'Trike',      label: 'Trike' },
  { value: 'Morgan1P',   label: 'Morgan (ballast)' },
  { value: 'Morgan2P',   label: 'Morgan (passenger)' },
];
const PASSENGER_REQUIRED_VEHICLE_TYPES = ['Sidecar', 'Trike', 'Morgan2P'];

// Matches the backend's is_3w() (secretary_routes.py) — Sidecar/Trike/Morgan2P (passenger)
// are priced on the 3-wheeler tier, Morgan1P (ballast) prices as a standard bike.
const isThreeWheeler = (vehicleType) => ['sidecar', 'trike', 'morgan2p'].includes((vehicleType || '').toLowerCase());

// Same 1st/2nd-machine pricing-tier logic as calc_machines in secretary_routes.py:
// bike and 3-wheeler counters are independent, ordered by machine entry order on the form.
// A day licence and a day membership are added on top; Give It A Go replaces
// the (one) machine's entry fee with its all-in price, which includes the
// membership. entryFees, licenceFee and membershipFee are the sub-totals.
function calcAmountDue(machines, prices, licenceOption, dayMembership) {
  if (!prices) return null;
  if (licenceOption === 'giag') {
    if (prices.giag == null) return { total: 0, entryFees: 0, licenceFee: 0, membershipFee: 0, missingPrice: true };
    return { total: prices.giag, entryFees: 0, licenceFee: prices.giag, membershipFee: 0, missingPrice: false };
  }
  let bikeN = 0, w3N = 0, total = 0, missingPrice = false;
  for (const m of machines) {
    if (isThreeWheeler(m.vehicleType)) {
      w3N++;
      const price = w3N === 1 ? prices.first_3w : prices.second_3w;
      if (price == null) { missingPrice = true; continue; }
      total += price;
    } else {
      bikeN++;
      const price = bikeN === 1 ? prices.first_bike : prices.second_bike;
      if (price == null) { missingPrice = true; continue; }
      total += price;
    }
  }
  const entryFees = Math.round(total * 100) / 100;
  const licenceFee = licenceOption === 'day_licence' ? (prices.day_licence ?? 0) : 0;
  if (licenceOption === 'day_licence' && prices.day_licence == null) missingPrice = true;
  const membershipFee = dayMembership ? (prices.day_membership ?? 0) : 0;
  if (dayMembership && prices.day_membership == null) missingPrice = true;
  return {
    total: Math.round((entryFees + licenceFee + membershipFee) * 100) / 100,
    entryFees, licenceFee, membershipFee, missingPrice,
  };
}


let machineSeq = 0;
const emptyMachine = () => ({
  id: `m${++machineSeq}`,
  model: '', vehicleType: 'Motorcycle', make: '', capacity: '', stroke: '', ridingNo: '',
  classFlags: { openCapacity: true, forgottenEra: false, roadLegal: false, vintage: false, classic: false },
  // Riding No. this machine's "fill in your bike?" suggestion has been dismissed for (by
  // editing a bike field by hand instead of accepting it) — re-armed automatically once
  // Riding No. itself changes, since it's compared against the current Riding No. below.
  bikeSuggestionDismissedFor: null,
});

const emptyPerson = () => ({
  surname: '', firstName: '', address: '', postcode: '',
  dob: '', tel: '', acuLicence: '', acuClub: 'NHCA',
});

// "Remember me" autofill — browser-local only, never transmitted anywhere except as part
// of an actual submission. Deliberately excludes payment (changes every entry) and the
// declaration/signature block (each entry needs its own fresh, explicit declaration).
const SAVED_PROFILE_KEY = 'nhca-entry-profile-v1';
const SAVED_PROFILE_VERSION = 1;

function loadSavedProfile() {
  try {
    const raw = localStorage.getItem(SAVED_PROFILE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== SAVED_PROFILE_VERSION || !parsed.data) return null;
    return parsed.data;
  } catch {
    return null; // corrupt JSON / localStorage unavailable — treat as "nothing saved"
  }
}

function saveProfile(data) {
  try {
    localStorage.setItem(SAVED_PROFILE_KEY, JSON.stringify({ version: SAVED_PROFILE_VERSION, data }));
  } catch {
    // private browsing / quota exceeded — this is a convenience feature, fail silently
  }
}

function clearSavedProfile() {
  try {
    localStorage.removeItem(SAVED_PROFILE_KEY);
  } catch {
    // ignore
  }
}

// Separate from the single-profile memory above: a rider can have several distinct bikes,
// each with their own preferred race number, so these are kept as a dict keyed by race
// number rather than a single snapshot. Deliberately excludes classFlags — which class(es)
// a machine can be entered in varies event to event, so blindly carrying that over could
// silently apply a class the current event doesn't even offer.
const SAVED_BIKES_KEY = 'nhca-saved-bikes-v1';
const SAVED_BIKES_VERSION = 1;

function loadSavedBikes() {
  try {
    const raw = localStorage.getItem(SAVED_BIKES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== SAVED_BIKES_VERSION || typeof parsed.data !== 'object' || !parsed.data) return {};
    return parsed.data;
  } catch {
    return {};
  }
}

function saveSavedBikes(bikesByRaceNumber) {
  try {
    localStorage.setItem(SAVED_BIKES_KEY, JSON.stringify({ version: SAVED_BIKES_VERSION, data: bikesByRaceNumber }));
  } catch {
    // ignore
  }
}

function clearSavedBikes() {
  try {
    localStorage.removeItem(SAVED_BIKES_KEY);
  } catch {
    // ignore
  }
}

const DECLARATIONS = [
  'I hereby declare that I have had the opportunity to read, and that I understand the National Sporting Code of the ACU, the ACU Standing Regulations, such Supplementary Regulations as have or may be issued for the event and agree to be bound by them.',
  'I declare that I am physically and mentally fit to take part in the event and I am competent to do so. I confirm that I understand the nature and type of event I am entering and its inherent risks and agree to accept the same notwithstanding that such risks may involve negligence on the part of the organisers or officials.',
  'I confirm that I am not currently suspended from ACU permitted competition nor on the ACU Stop List as a result of incurring a Concussion injury.',
  'I accept that insurance arranged on my behalf by the organisers of events that I may enter specifically excludes liability between the participants. I understand that this form may be used in litigation as evidence that any serious injury will be principally the result of my voluntary decision to engage in a high-risk activity.',
  'I consent to details of any injuries I may suffer at this event being passed between all medical services and the Clerk of the Course.',
  'I consent to the collection and retention of my personal information by the ACU.',
  'I consent to NHCA collecting, holding and using the personal information I provide on this form (including my contact details, entry details and results) for the purposes of organising and running NHCA events and the National Hill Climb Championship — including contacting me about events I have entered, and publishing my results, times and championship standings on the NHCA website and in event programmes/reports.',
  'I confirm that the machine(s) as described below which I shall participate on shall be suitable and proper for the purpose. I confirm that I am eligible to participate on the machines for which I have entered.',
  'I confirm that if any part of the event takes place on a public highway, the machine(s) described below shall be insured as required by the Road Traffic Acts, or equivalent legislation, and that they will comply with the regulations in respect thereof.',
  'I accept responsibility for any items borrowed from the Organiser during the course of the event, and understand I am liable for the cost of replacement of any items lost or not returned.',
  'I confirm that I have not been refused an ACU Licence, nor had an ACU Licence suspended, nor have I been excluded from any ACU competition.',
  'I have read and understood The Auto Cycle Union Ltd Data Protection Policy and consent to the collection and retention of my personal information by the ACU.',
];

const RISK_ACKNOWLEDGEMENT = 'I understand that by taking part in this event I am exposed to a risk of death, becoming permanently disabled or suffering some other serious injury and I acknowledge that even in the event that negligence on the part of the ACU, any event organiser, any circuit owner, the promoter, the organising club, the venue owner, or any individual carrying out duties on their behalf were to be a contributory cause of any serious injury I may suffer, the dominant cause of any serious injury will always be my voluntary decision to take part in a high-risk activity. I have read the above and acknowledge that my participation in motorsport is entirely at my own risk. I agree that I am required to register on arrival by "signing on" at the designated place before taking part in any Practice Session or Race.';

export default function OnlineEntryForm() {
  const calendarId = window.location.pathname.split('/').filter(Boolean).pop();

  const [event, setEvent]     = useState(null);
  const [eventError, setEventError] = useState(null);

  const [rider, setRider]           = useState({ ...emptyPerson(), email: '' });
  const [hasPassenger, setHasPassenger] = useState(false);
  const [passenger, setPassenger]   = useState(emptyPerson());
  const [riderEmergencyContact, setRiderEmergencyContact] = useState('');
  const [passengerEmergencyContact, setPassengerEmergencyContact] = useState('');

  const [entrantDiffers, setEntrantDiffers] = useState(false);
  const [entrant, setEntrant] = useState({ name: '', address: '', licenceNo: '' });

  const [machines, setMachines] = useState([emptyMachine()]);

  const [payment, setPayment] = useState({ method: 'bacs', amount: '', bankReference: '', coversOthers: false, coveredByCode: '' });
  const [secretaryNote, setSecretaryNote] = useState('');
  // The ACU licence and NHCA membership answers. option: null = not answered
  // yet, '' = has an ACU licence, 'day_licence' or 'giag'. dayMembership:
  // null = not answered yet, false = a member, true = buying a day
  // membership. Both must be answered (where offered), and are remembered
  // with "save my details", except Give It A Go, which is once per person.
  const [licence, setLicence] = useState({ option: null, firstTimer: false, dayMembership: null });

  const [signature, setSignature] = useState({
    riderName: '', riderDate: '',
    passengerName: '', passengerDate: '',
    parentGuardianName: '', parentGuardianDate: '',
    agreed: false,
  });

  const [errors, setErrors] = useState([]);
  // Per-field validation, shown inline as soon as a field is blurred ("tab
  // away"), not just in the big list on Submit/Print. `touched` tracks which
  // fields the entrant has already left at least once; `submitAttempted`
  // reveals every field's error at once after a failed Submit/Print, same as
  // touching them all. fieldErrors/machineFieldErrors below are recomputed
  // every render (cheap string checks) rather than stored — a single source
  // of truth shared with validate()'s own summary list, so the two can never
  // drift out of sync with each other.
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const touch = (key) => setTouched(t => (t[key] ? t : { ...t, [key]: true }));
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [, setEditToken] = useState(null);
  const [coverageCode, setCoverageCode] = useState(null);
  const [website, setWebsite] = useState(''); // honeypot — real users never see or fill this in
  const [rememberMe, setRememberMe] = useState(false);
  const [profileLoadedNotice, setProfileLoadedNotice] = useState(false);
  const [savedBikes, setSavedBikes] = useState({}); // { [raceNumber]: { model, vehicleType, make, capacity, stroke } }

  useEffect(() => {
    if (!calendarId || isNaN(calendarId)) { setEventError('No valid event ID in URL.'); return; }
    fetch(`/api/events/${calendarId}/header`, { headers: { 'X-API-Key': API_KEY } })
      .then(async res => {
        const json = await res.json().catch(() => null);
        if (!res.ok) throw new Error((json && json.error) || 'Event details not available');
        return json;
      })
      .then(setEvent)
      .catch(err => setEventError(err.message));
  }, [calendarId]);

  // Load the per-race-number bike registry so Riding No. suggestions work immediately.
  useEffect(() => {
    setSavedBikes(loadSavedBikes());
  }, []);

  // Prefill from a previously-saved local profile, if any — see saveProfile() in handleSubmit.
  useEffect(() => {
    const saved = loadSavedProfile();
    if (!saved) return;
    if (saved.rider) setRider(r => ({ ...r, ...saved.rider }));
    if (typeof saved.hasPassenger === 'boolean') setHasPassenger(saved.hasPassenger);
    if (saved.passenger) setPassenger(p => ({ ...p, ...saved.passenger }));
    if (typeof saved.entrantDiffers === 'boolean') setEntrantDiffers(saved.entrantDiffers);
    if (saved.entrant) setEntrant(en => ({ ...en, ...saved.entrant }));
    if (typeof saved.riderEmergencyContact === 'string') setRiderEmergencyContact(saved.riderEmergencyContact);
    if (typeof saved.passengerEmergencyContact === 'string') setPassengerEmergencyContact(saved.passengerEmergencyContact);
    if (saved.licence) {
      setLicence(l => ({
        ...l,
        option: ['', 'day_licence'].includes(saved.licence.option) ? saved.licence.option : null,
        dayMembership: typeof saved.licence.dayMembership === 'boolean' ? saved.licence.dayMembership : null,
      }));
    }
    if (Array.isArray(saved.machines) && saved.machines.length > 0) {
      setMachines(saved.machines.map(m => {
        const base = emptyMachine(); // fresh id via machineSeq — never reuse a saved id
        return { ...base, ...m, classFlags: { ...base.classFlags, ...(m.classFlags || {}) } };
      }));
    }
    setRememberMe(true);
    setProfileLoadedNotice(true);
  }, []);

  const handleClearSavedDetails = () => {
    clearSavedProfile();
    clearSavedBikes();
    setSavedBikes({});
    setRememberMe(false);
    setProfileLoadedNotice(false);
  };

  // Fail fast with a reason instead of rendering a form POST /api/entries/submit
  // will reject anyway. Only evaluated once `event` has actually loaded — while
  // it's still null (loading, or a fetch error), this stays false so the
  // existing "show the form while loading" behaviour is unchanged (matches the
  // already-documented, deliberately-untouched eventError gap elsewhere in this
  // file — not fixing that here, just not making it worse).
  const eventYear = event?.date ? new Date(`${event.date}T00:00:00`).getFullYear() : null;
  const yearNotReady = eventYear !== null && eventYear < ONLINE_ENTRY_FIRST_YEAR;
  const missingReadiness = useMemo(() => (event ? missingReadinessFields(event) : []), [event]);
  // No entries while the season's calendar is still provisional.
  const seasonProvisional = !!event && !yearNotReady && !event.season_confirmed;
  // The organiser takes this event's entries themselves, not this website
  const externalEntries = !!event?.external_entries;
  const externalEntryHref = externalEntries ? (event.entry_form_url ||
    (event.has_entry_form_pdf ? `/api/calendar-editor/events/${calendarId}/pdf/entry-form` : null)) : null;
  const notReady = !!event && (externalEntries || yearNotReady || seasonProvisional || missingReadiness.length > 0);

  // Only offer the classes this event actually runs (calendar.roadlegal / forgotten_era / vc) —
  // Open Capacity has no event-level flag, it's always available. Show everything while the
  // event header is still loading rather than blocking the form on it.
  const visibleClassFlags = useMemo(() => {
    if (!event) return CLASS_FLAGS;
    return CLASS_FLAGS.filter(f => {
      if (f.key === 'openCapacity') return true;
      if (f.key === 'forgottenEra') return !!event.classes?.forgotten_era;
      if (f.key === 'roadLegal') return !!event.classes?.road_legal;
      if (f.key === 'vintage' || f.key === 'classic') return !!event.classes?.vintage_classic;
      return true;
    });
  }, [event]);

  const dayLicenceOffered = event?.prices?.day_licence != null;
  const giagOffered = !!event?.giag && event?.prices?.giag != null;
  const dayMembershipOffered = event?.prices?.day_membership != null;
  // Only ask what this event offers: with no day licence or GIAG, everyone
  // has an ACU licence; with no day membership price, everyone is a member
  // (and Give It A Go already includes one).
  const askLicence = dayLicenceOffered || giagOffered;
  // A remembered answer this event doesn't offer counts as not answered.
  const licenceAnswer = (licence.option === '' || (licence.option === 'day_licence' && dayLicenceOffered)
    || (licence.option === 'giag' && giagOffered)) ? licence.option : null;
  const licenceOption = askLicence ? (licenceAnswer ?? '') : '';
  const askMembership = dayMembershipOffered && licenceOption !== 'giag';
  const dayMembership = askMembership && licence.dayMembership === true;
  const amountDue = useMemo(
    () => calcAmountDue(machines, event?.prices, licenceOption, dayMembership),
    [machines, event, licenceOption, dayMembership]);
  // The bank transfer amount last filled in for the entrant, so a later
  // change (adding a day licence, say) can update it while they haven't typed
  // their own figure.
  const autoAmountRef = useRef('');

  // Default the bank-transfer AMOUNT to the computed Amount Due — while the field is
  // blank or still holds the last figure filled in here (so adding a day licence
  // updates it), never over a manually-entered figure (e.g. a group/family
  // payment covering more than this one entry). Cheque payers aren't defaulted; amounts
  // there are more often rounded/adjusted by hand.
  useEffect(() => {
    if (payment.method !== 'bacs' || !amountDue || amountDue.missingPrice) return;
    const next = amountDue.total.toFixed(2);
    if (payment.amount !== '' && payment.amount !== autoAmountRef.current) return; // their own figure
    autoAmountRef.current = next;
    if (payment.amount !== next) setPayment(p => ({ ...p, amount: next }));
  }, [payment.method, payment.amount, amountDue]);

  // Bank transfer reference is calculated, not typed — riders_acu_number-last_four_digits_of_acu_permit-day
  // (eg "1234-5678-Sat") so the secretary can match a bank statement line to a rider/event without
  // relying on free text the entrant types themselves.
  const paymentReference = useMemo(
    () => computePaymentReference(rider.acuLicence, event, rider.surname, licenceOption),
    [rider.acuLicence, rider.surname, licenceOption, event]
  );
  useEffect(() => {
    if (payment.method === 'bacs') {
      setPayment(p => (p.bankReference === paymentReference ? p : { ...p, bankReference: paymentReference }));
    }
  }, [payment.method, paymentReference]);

  // Only cap at one machine when NEITHER a 2nd bike nor a 2nd 3-wheeler price
  // is configured at all — that's the real signal this event doesn't take more
  // than one machine per entry. An event with only one of the two set (e.g.
  // 2nd-bike price but no 2nd-3-wheeler price) still allows more machines;
  // calcAmountDue already flags missingPrice per combination and falls back to
  // "Contact secretary for amount" rather than silently miscalculating, so
  // there's no need to block adding a machine just because one specific
  // combo isn't priced. Unknown/still-loading prices are never treated as a cap.
  const maxMachines = useMemo(() => {
    if (licenceOption === 'giag') return 1;
    if (!event?.prices) return Infinity;
    const { second_bike, second_3w } = event.prices;
    return (second_bike == null && second_3w == null) ? 1 : Infinity;
  }, [event, licenceOption]);
  const atMachineCap = machines.length >= maxMachines;

  const riderEmailSuggestion = useMemo(() => suggestEmailDomain(rider.email), [rider.email]);
  const riderAge     = useMemo(() => ageOnDate(rider.dob, event?.date), [rider.dob, event]);
  const passengerAge = useMemo(() => ageOnDate(passenger.dob, event?.date), [passenger.dob, event]);
  const riderUnder18     = riderAge !== null && riderAge < 18;
  const passengerUnder18 = hasPassenger && passengerAge !== null && passengerAge < 18;
  const needsParentGuardian = riderUnder18 || passengerUnder18;

  // Fields that identify "the bike" for the per-race-number suggestion — editing any of
  // these by hand (rather than accepting the suggestion) means the entrant is deliberately
  // diverging from the saved bike for this Riding No., so the prompt shouldn't keep
  // reappearing and pushing them back towards the old values. Riding No. itself is excluded:
  // changing it is what should (re)trigger the suggestion, not suppress it.
  const BIKE_SUGGESTION_FIELDS = ['model', 'vehicleType', 'make', 'capacity', 'stroke'];

  const updateMachine = (id, field, value) => {
    setMachines(ms => ms.map(m => {
      if (m.id !== id) return m;
      const next = { ...m, [field]: value };
      if (BIKE_SUGGESTION_FIELDS.includes(field)) next.bikeSuggestionDismissedFor = m.ridingNo.trim();
      return next;
    }));
  };
  const toggleMachineClassFlag = (id, flagKey) => {
    setMachines(ms => ms.map(m => {
      if (m.id !== id) return m;
      const next = { ...m.classFlags, [flagKey]: !m.classFlags[flagKey] };
      // Vintage and Classic are mutually exclusive — a machine can't be entered as both.
      if (flagKey === 'vintage' && next.vintage) next.classic = false;
      if (flagKey === 'classic' && next.classic) next.vintage = false;
      return { ...m, classFlags: next };
    }));
  };
  const addMachine = () => setMachines(ms => [...ms, emptyMachine()]);
  const removeMachine = (id) => setMachines(ms => (ms.length === 1 ? ms : ms.filter(m => m.id !== id)));

  const getSavedBikeForRidingNo = (ridingNo) => {
    const key = ridingNo.trim();
    return key ? (savedBikes[key] || null) : null;
  };

  // Returns the saved bike for this machine's Riding No. only when there's actually
  // something to fill in — not when the fields already match (already applied / typed
  // by hand) — distinct from getSavedBikeForRidingNo, which is also used to decide
  // whether to show "forget this bike" even after the suggestion's been accepted.
  const getMatchedBike = (m) => {
    const saved = getSavedBikeForRidingNo(m.ridingNo);
    if (!saved) return null;
    if (m.bikeSuggestionDismissedFor === m.ridingNo.trim()) return null;
    const alreadyApplied = saved.model === m.model && saved.vehicleType === m.vehicleType &&
      saved.make === m.make && saved.capacity === m.capacity && saved.stroke === m.stroke;
    return alreadyApplied ? null : saved;
  };

  const applyBikeSuggestion = (id, bike) => {
    setMachines(ms => ms.map(m => (m.id === id
      ? { ...m, model: bike.model, vehicleType: bike.vehicleType, make: bike.make, capacity: bike.capacity, stroke: bike.stroke }
      : m
    )));
  };

  const forgetSavedBike = (raceNumber) => {
    setSavedBikes(prev => {
      const next = { ...prev };
      delete next[raceNumber];
      saveSavedBikes(next);
      return next;
    });
  };

  const machineLabel = (m, i) => {
    const bits = [`Machine ${i + 1}`];
    const classes = CLASS_FLAGS.filter(f => m.classFlags[f.key]).map(f => f.label);
    if (classes.length) bits.push(classes.join(', '));
    if (m.ridingNo) bits.push(`No. ${m.ridingNo}`);
    return bits.join(' — ');
  };

  // Single source of truth for every "one field, one message" validation
  // rule — recomputed each render (cheap string checks), read both by
  // validate()'s full-form summary list and by each field's own inline
  // <FieldError> once touched. Keeping this as one object/array means the
  // two can never say different things about the same field.
  const fieldErrors = {
    riderFirstName: !rider.firstName.trim() ? 'Rider first name is required.' : '',
    riderSurname: !rider.surname.trim() ? 'Rider surname is required.' : '',
    riderEmail: !rider.email.trim() ? 'Rider email address is required.'
      : (!isValidEmail(rider.email) ? 'Rider email address is not a valid format.' : ''),
    riderTel: (rider.tel.trim() && !isValidPhone(rider.tel)) ? 'Rider telephone number: 10-15 digits; spaces, dashes, brackets and a leading + are fine.' : '',
    riderDob: !rider.dob ? 'Rider date of birth is required.' : '',
    riderAcuLicence: !rider.acuLicence.trim()
      ? (licenceOption ? '' : `Rider ACU Licence / Registration number is required${askLicence ? ' (or choose a day licence or Give It A Go below)' : ''}.`)
      : (!isValidAcuLicence(rider.acuLicence) ? 'Rider ACU Licence / Registration number must contain only digits.' : ''),
    licenceChoice: (askLicence && licenceAnswer === null) ? 'Please say whether you have an ACU licence.' : '',
    membershipChoice: (askMembership && licence.dayMembership === null) ? 'Please say whether you are an NHCA member.' : '',
    licenceFirstTimer: (licenceOption === 'giag' && !licence.firstTimer)
      ? "Give It A Go is for first-timers: please confirm you haven't competed in a hill climb before." : '',
    riderEmergencyContact: !riderEmergencyContact.trim() ? "Rider's emergency contact details are required." : '',
    passengerTel: (hasPassenger && passenger.tel.trim() && !isValidPhone(passenger.tel)) ? 'Passenger telephone number: 10-15 digits; spaces, dashes, brackets and a leading + are fine.' : '',
    passengerAcuLicence: (hasPassenger && passenger.acuLicence.trim() && !isValidAcuLicence(passenger.acuLicence)) ? 'Passenger ACU Licence / Registration number must contain only digits.' : '',
    passengerEmergencyContact: (hasPassenger && !passengerEmergencyContact.trim()) ? "Passenger's emergency contact details are required." : '',
    paymentAmount: (payment.method !== 'covered' && (!payment.amount || Number(payment.amount) <= 0)) ? 'Payment amount is required.' : '',
    paymentCoveredByCode: (payment.method === 'covered' && !payment.coveredByCode.trim()) ? 'A coverage code is required — enter the code the payer shared with you.' : '',
    signatureRiderName: !signature.riderName.trim() ? "Rider's typed signature is required." : '',
    signaturePassengerName: (hasPassenger && !signature.passengerName.trim()) ? "Passenger's typed signature is required." : '',
    signatureParentGuardianName: (needsParentGuardian && !signature.parentGuardianName.trim()) ? 'Parent/guardian typed signature is required for an under-18 rider or passenger.' : '',
    signatureAgreed: !signature.agreed ? 'You must confirm agreement to the declarations below.' : '',
  };

  // Per-machine equivalent — keyed by index (machines.map already uses m.id
  // as the React key; this array is positional, same order).
  const machineFieldErrors = machines.map(m => ({
    classFlags: !Object.values(m.classFlags).some(Boolean) ? 'Select at least one class.' : '',
    ridingNo: (m.ridingNo.trim() && !isValidDigits(m.ridingNo)) ? 'Riding No. must contain only digits.' : '',
    make: !m.make.trim() ? 'Make is required.' : '',
    capacity: !m.capacity.trim() ? 'Capacity (cc) is required.'
      : (!isValidDigits(m.capacity) ? 'Capacity (cc) must contain only digits.' : ''),
    stroke: (m.stroke.trim() && !isValidDecimal(m.stroke)) ? 'Stroke (mm) must be a number (a decimal point is allowed).' : '',
    vehicleType: (PASSENGER_REQUIRED_VEHICLE_TYPES.includes(m.vehicleType) && !hasPassenger)
      ? 'This vehicle type requires a passenger — add passenger details above.' : '',
  }));

  const validate = () => {
    const errs = Object.values(fieldErrors).filter(Boolean);
    if (machines.length > maxMachines) {
      errs.push(licenceOption === 'giag'
        ? 'Give It A Go covers one machine only — remove additional machine entries.'
        : "This event's pricing only covers one machine per entry — remove additional machine entries.");
    }
    machineFieldErrors.forEach((fe, i) => {
      if (fe.classFlags) errs.push(`Machine ${i + 1}: ${fe.classFlags}`);
      if (fe.ridingNo) errs.push(`Machine ${i + 1}: ${fe.ridingNo}`);
      if (fe.make) errs.push(`Machine ${i + 1}: ${fe.make}`);
      if (fe.capacity) errs.push(`Machine ${i + 1}: ${fe.capacity}`);
      if (fe.stroke) errs.push(`Machine ${i + 1}: ${fe.stroke}`);
      if (fe.vehicleType) errs.push(`Machine ${i + 1}: ${fe.vehicleType}`);
    });
    if (hasPassenger && !machines.some(m => PASSENGER_REQUIRED_VEHICLE_TYPES.includes(m.vehicleType))) {
      errs.push('A passenger has been added, but no machine entry is a Sidecar, Trike, or Morgan (passenger) — update the vehicle type or remove the passenger.');
    }
    if (payment.method === 'bacs' && !payment.bankReference.trim()) errs.push('Unable to calculate a bank transfer reference — check the Rider ACU Licence number (or surname) above, or wait for event details to finish loading.');
    return errs;
  };

  // Shared by Submit and Print — both are valid "I've finished this entry" completions
  // (cheque-payers use Print instead of Submit, since they can't pay online), so remembering
  // details needs to happen on either path, not just Submit.
  const persistRememberedProfile = () => {
    if (rememberMe) {
      saveProfile({
        rider, hasPassenger, passenger, entrantDiffers, entrant,
        riderEmergencyContact, passengerEmergencyContact,
        machines: machines.map(({ model, vehicleType, make, capacity, stroke, ridingNo, classFlags }) => (
          { model, vehicleType, make, capacity, stroke, ridingNo, classFlags }
        )),
        // Give It A Go is once per person, so next time is asked afresh.
        licence: licenceOption === 'giag'
          ? { option: null, dayMembership: null }
          : { option: licence.option, dayMembership: licence.dayMembership },
      });
      const updatedBikes = { ...savedBikes };
      machines.forEach(m => {
        const key = m.ridingNo.trim();
        if (!key) return;
        updatedBikes[key] = { model: m.model, vehicleType: m.vehicleType, make: m.make, capacity: m.capacity, stroke: m.stroke };
      });
      saveSavedBikes(updatedBikes);
      setSavedBikes(updatedBikes);
    } else {
      clearSavedProfile();
      clearSavedBikes();
      setSavedBikes({});
    }
  };

  // signatureOverride lets the caller pass a freshly-computed signature object
  // (e.g. with riderDate defaulted to today) without waiting for the setState
  // call that updates it to actually re-render first.
  const buildSubmissionPayload = (signatureOverride = signature) => ({
    calendar_id: Number(calendarId),
    rider, passenger, hasPassenger,
    riderEmergencyContact, passengerEmergencyContact,
    entrantDiffers, entrant,
    machines: machines.map(({ model, vehicleType, make, capacity, stroke, ridingNo, classFlags }) => (
      { model, vehicleType, make, capacity, stroke, ridingNo, classFlags }
    )),
    payment,
    licence: { option: licenceOption, firstTimer: licence.firstTimer, dayMembership },
    secretaryNote,
    signature: signatureOverride,
    declarationVersion: DECLARATION_VERSION,
    declarationText: JSON.stringify({ declarations: DECLARATIONS, riskAcknowledgement: RISK_ACKNOWLEDGEMENT }),
    website,
  });

  // One submission per page load. `submitted` locks the buttons once an
  // entry is in, but React state only updates on the next render, so two
  // Enter presses a few milliseconds apart would both see submitting=false.
  // The ref is set synchronously and closes that gap. The server also
  // treats an identical resubmission within 15 minutes as the same entry
  // (see submit_entry), as a backstop.
  const inFlightRef = useRef(false);

  // Validates and submits; returns true once the entry is in (or already was).
  const submitOnce = async () => {
    if (submitted) return true;
    if (inFlightRef.current) return false;
    setSubmitAttempted(true);
    const errs = validate();
    setErrors(errs);
    if (errs.length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }

    inFlightRef.current = true;
    const effectiveSignature = { ...signature, riderDate: signature.riderDate || todayIso() };
    setSignature(effectiveSignature);
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await postJson('/api/entries/submit', buildSubmissionPayload(effectiveSignature));
      setEditToken(result.edit_token || null);
      setCoverageCode(result.coverage_code || null);
      setSubmitted(true);
      persistRememberedProfile();
      return true;
    } catch (err) {
      setSubmitError(err.message);
      inFlightRef.current = false;
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitted) return;
    await submitOnce();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Print submits silently before printing, rather than print-only:
  // cheque-payers use Print instead of Submit specifically because they can't
  // pay online, but they still need a real entry_submissions row — printing
  // without submitting would leave that payment path with no electronic
  // record at all. Once submitted, Print just prints again.
  const handlePrint = async () => {
    if (await submitOnce()) {
      window.print();
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="secretary-portal App entry-form-page">
      <header className="App-header no-print">
        <h1>🏍️ NHCA Online Entry</h1>
        <p>National Hillclimb Association</p>
      </header>

      <main className="secretary-main">
        <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
          <div className="sec-section-header">
            <div>
              <h2>🏁 {event?.event || (eventError ? 'Event not found' : 'Loading event…')}</h2>
              {event && (
                <p style={{ color: 'var(--text-light)', margin: '0.25rem 0 0', fontSize: '1rem' }}>
                  {fmtDate(event.date)}
                </p>
              )}
              {event && (
                <p className="event-meta-row" style={{ color: 'var(--text-light)', margin: '0.4rem 0 0', fontSize: '0.85rem' }}>
                  <span><strong>Event:</strong> Hillclimb</span>
                  <span><strong>Venue:</strong> {event.hill}</span>
                  <span><strong>Permit No:</strong> {event.acu_permit || '—'}</span>
                  <span><strong>Course Lic/Cert No:</strong> {event.course_licence_no || '—'}</span>
                </p>
              )}
              {eventError && (
                <p style={{ color: 'var(--danger-color)', margin: '0.4rem 0 0', fontSize: '0.9rem' }}>{eventError}</p>
              )}
            </div>
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
            Organisers: NHCA. This event is held under the National Sporting Code of the Auto-Cycle Union, the
            Standing Regulations, Supplementary Regulations and any Final Instructions issued for the meeting.
          </p>
        </div>

        {externalEntries ? (
          <div className="sec-section" style={{ borderLeft: '4px solid var(--warning-color)', marginBottom: '1.25rem' }}>
            <h3 style={{ marginTop: 0 }}>Entries for this event are handled by the organiser</h3>
            <p style={{ fontSize: '0.9rem' }}>
              This event doesn&apos;t take entries through this website.{' '}
              {externalEntryHref
                ? <>Please use the organiser&apos;s <a href={externalEntryHref} target="_blank" rel="noreferrer">entry form</a>.</>
                : <>Please contact the organiser to enter.</>}
            </p>
          </div>
        ) : notReady ? (
          <div className="sec-section" style={{ borderLeft: '4px solid var(--warning-color)', marginBottom: '1.25rem' }}>
            <h3 style={{ marginTop: 0 }}>This event isn&apos;t open for online entry yet</h3>
            {yearNotReady && (
              <p style={{ fontSize: '0.9rem' }}>
                Online entry starts with the {ONLINE_ENTRY_FIRST_YEAR} season — please use the paper entry form for this event.
              </p>
            )}
            {seasonProvisional && (
              <p style={{ fontSize: '0.9rem' }}>
                The {eventYear} calendar is still provisional. Online entry opens once it has been confirmed.
                Please check back later.
              </p>
            )}
            {!seasonProvisional && missingReadiness.length > 0 && (
              <p style={{ fontSize: '0.9rem' }}>
                This event is still being set up for online entry. Missing: {missingReadiness.map(f => f.label).join(', ')}.
                Please check back later, or contact the event secretary.
              </p>
            )}
          </div>
        ) : (
        <>
        {profileLoadedNotice && (
          <div className="sec-section no-print" style={{ borderLeft: '4px solid var(--text-light)', marginBottom: '1.25rem' }}>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              We&apos;ve filled this in from your last entry on this device — not you?{' '}
              <button type="button" className="email-suggestion-btn" onClick={handleClearSavedDetails}>Clear saved details</button>
              {' '}or{' '}
              <button type="button" className="email-suggestion-btn" onClick={() => setProfileLoadedNotice(false)}>dismiss</button>.
            </p>
          </div>
        )}

        {errors.length > 0 && (
          <div className="sec-section no-print" style={{ borderLeft: '4px solid var(--danger-color)', marginBottom: '1.25rem' }}>
            <h3 style={{ color: 'var(--danger-color)', marginTop: 0 }}>⚠️ Please fix the following</h3>
            <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
              {errors.map((er, i) => <li key={i}>{er}</li>)}
            </ul>
          </div>
        )}

        {submitError && (
          <div className="sec-section no-print" style={{ borderLeft: '4px solid var(--danger-color)', marginBottom: '1.25rem' }}>
            <h3 style={{ color: 'var(--danger-color)', marginTop: 0 }}>⚠️ Couldn&apos;t submit your entry</h3>
            <p style={{ fontSize: '0.9rem' }}>{submitError}</p>
          </div>
        )}

        {submitted && (
          <div className="sec-section no-print" style={{ borderLeft: '4px solid var(--success-color)', marginBottom: '1.25rem' }}>
            <h3 style={{ color: 'var(--success-color)', marginTop: 0 }}>✅ Entry received</h3>
            <p style={{ fontSize: '0.9rem' }}>
              Your entry has been received and is pending review by the secretary. A confirmation has been emailed to <strong>{rider.email}</strong>.
            </p>
            {coverageCode && (
              <div style={{ marginTop: '0.75rem' }}>
                <p style={{ fontSize: '0.9rem', marginBottom: '0.4rem' }}>
                  Your coverage code, to share with anyone whose entry fee you&apos;re covering — they&apos;ll enter it on
                  their own form instead of paying themselves:
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <strong style={{ fontSize: '1.1rem', letterSpacing: '0.05em' }}>{coverageCode}</strong>
                  <button
                    type="button"
                    onClick={async () => {
                      // navigator.clipboard needs a secure context (HTTPS or
                      // localhost) — unavailable e.g. over a plain-HTTP LAN IP,
                      // where it's undefined and throws before any fallback
                      // runs. Fall back to a prompt() so the code is still
                      // selectable/copyable by hand either way.
                      try {
                        if (!navigator.clipboard) throw new Error('Clipboard API unavailable');
                        await navigator.clipboard.writeText(coverageCode);
                        alert('Coverage code copied to clipboard!');
                      } catch {
                        window.prompt('Copy this coverage code:', coverageCode);
                      }
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            name="website"
            value={website}
            onChange={e => setWebsite(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
          />

          {/* Rider */}
          <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
            <div className="form-section-label">Rider</div>
            <div className="form-grid">
              <div className="form-row">
                <div className="form-group">
                  <label>First name(s)</label>
                  <input type="text" maxLength={60} value={rider.firstName} onChange={e => setRider({ ...rider, firstName: e.target.value })} onBlur={() => touch('riderFirstName')} />
                  <FieldError show={touched.riderFirstName || submitAttempted} message={fieldErrors.riderFirstName} />
                </div>
                <div className="form-group">
                  <label>Surname</label>
                  <input type="text" maxLength={60} value={rider.surname} onChange={e => setRider({ ...rider, surname: e.target.value })} onBlur={() => touch('riderSurname')} />
                  <FieldError show={touched.riderSurname || submitAttempted} message={fieldErrors.riderSurname} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email address <span className="req-tag">(for confirmation)</span></label>
                  <input type="email" maxLength={100} value={rider.email} onChange={e => setRider({ ...rider, email: e.target.value })} onBlur={() => touch('riderEmail')} />
                  <FieldError show={touched.riderEmail || submitAttempted} message={fieldErrors.riderEmail} />
                  {riderEmailSuggestion && (
                    <span className="muted-note no-print">
                      Did you mean{' '}
                      <button type="button" className="email-suggestion-btn" onClick={() => setRider({ ...rider, email: riderEmailSuggestion })}>
                        {riderEmailSuggestion}
                      </button>?
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Telephone</label>
                  <input type="tel" maxLength={25} value={rider.tel} onChange={e => setRider({ ...rider, tel: e.target.value })} onBlur={() => touch('riderTel')} />
                  <FieldError show={touched.riderTel || submitAttempted} message={fieldErrors.riderTel} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Address</label>
                  <input type="text" maxLength={120} value={rider.address} onChange={e => setRider({ ...rider, address: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Postcode</label>
                  <input type="text" maxLength={10} value={rider.postcode} onChange={e => setRider({ ...rider, postcode: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date of birth</label>
                  <DobSelect value={rider.dob} onChange={dob => setRider({ ...rider, dob })} onBlur={() => touch('riderDob')} />
                  <FieldError show={touched.riderDob || submitAttempted} message={fieldErrors.riderDob} />
                  {riderUnder18 && <span className="under18-note">Under 18 on event date — parent/guardian consent required below</span>}
                </div>
                <div className="form-group">
                  <label>ACU Licence / Registration No.</label>
                  <input type="text" maxLength={30} value={rider.acuLicence} onChange={e => setRider({ ...rider, acuLicence: e.target.value })} onBlur={() => touch('riderAcuLicence')} />
                  <FieldError show={touched.riderAcuLicence || submitAttempted} message={fieldErrors.riderAcuLicence} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>ACU Affiliated Club</label>
                  <input type="text" maxLength={60} value={rider.acuClub} onChange={e => setRider({ ...rider, acuClub: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Rider&apos;s emergency contact (name &amp; number)</label>
                  <input type="text" maxLength={100} value={riderEmergencyContact} onChange={e => setRiderEmergencyContact(e.target.value)} onBlur={() => touch('riderEmergencyContact')} />
                  <FieldError show={touched.riderEmergencyContact || submitAttempted} message={fieldErrors.riderEmergencyContact} />
                </div>
              </div>
            </div>
          </div>

          {/* ACU licence and NHCA membership: each must be answered where offered */}
          {(askLicence || dayMembershipOffered) && (
            <div className="sec-section licence-section" style={{ marginBottom: '1.25rem' }}>
              <div className="form-section-label">ACU licence and NHCA membership</div>
              {askLicence && (
                <div role="radiogroup" aria-label="Do you have an ACU licence?">
                  <div className="licence-question">Do you have an ACU licence?</div>
                  <div className="payment-method-choice licence-choice">
                    <label className="inline-checkbox">
                      <input type="radio" name="licence" checked={licence.option === ''}
                        onChange={() => setLicence(l => ({ ...l, option: '', firstTimer: false }))} />
                      Yes, I have an ACU licence
                    </label>
                    {dayLicenceOffered && (
                      <label className="inline-checkbox">
                        <input type="radio" name="licence" checked={licence.option === 'day_licence'}
                          onChange={() => setLicence(l => ({ ...l, option: 'day_licence', firstTimer: false }))} />
                        No: a day licence, £{event.prices.day_licence.toFixed(2)}, added to your entry fees
                      </label>
                    )}
                    {giagOffered && (
                      <label className="inline-checkbox">
                        <input type="radio" name="licence" checked={licence.option === 'giag'}
                          onChange={() => setLicence(l => ({ ...l, option: 'giag' }))} />
                        No, and I&apos;m new: Give It A Go, £{event.prices.giag.toFixed(2)} all-in (day licence, day NHCA membership and one machine&apos;s entry)
                      </label>
                    )}
                  </div>
                  <FieldError show={submitAttempted} message={fieldErrors.licenceChoice} />
                </div>
              )}
              {licenceOption === 'giag' && (
                <div style={{ marginTop: '0.5rem' }}>
                  <label className="inline-checkbox">
                    <input type="checkbox" checked={licence.firstTimer}
                      onChange={e => setLicence(l => ({ ...l, firstTimer: e.target.checked }))}
                      onBlur={() => touch('licenceFirstTimer')} />
                    I have not competed in a hill climb before
                  </label>
                  <div className="muted-note">Give It A Go is a one-off offer for first-timers, once per person.</div>
                  <FieldError show={touched.licenceFirstTimer || submitAttempted} message={fieldErrors.licenceFirstTimer} />
                </div>
              )}
              {licenceOption && (
                <p className="muted-note" style={{ marginTop: '0.5rem' }}>
                  You don&apos;t need an ACU licence number. Leave it blank if you don&apos;t have one.
                </p>
              )}
              {askMembership && (
                <div role="radiogroup" aria-label="Are you an NHCA member?" style={{ marginTop: askLicence ? '0.75rem' : 0 }}>
                  <div className="licence-question">Are you an NHCA member?</div>
                  <div className="payment-method-choice licence-choice">
                    <label className="inline-checkbox">
                      <input type="radio" name="membership" checked={licence.dayMembership === false}
                        onChange={() => setLicence(l => ({ ...l, dayMembership: false }))} />
                      Yes, I&apos;m an NHCA member
                    </label>
                    <label className="inline-checkbox">
                      <input type="radio" name="membership" checked={licence.dayMembership === true}
                        onChange={() => setLicence(l => ({ ...l, dayMembership: true }))} />
                      No: a day membership, £{event.prices.day_membership.toFixed(2)}, added to your entry fees
                    </label>
                  </div>
                  <FieldError show={submitAttempted} message={fieldErrors.membershipChoice} />
                </div>
              )}
            </div>
          )}

          {/* Passenger */}
          <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
            <div className="form-section-label" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              Passenger
              <label className="inline-checkbox no-print">
                <input type="checkbox" checked={hasPassenger} onChange={e => setHasPassenger(e.target.checked)} />
                This entry has a passenger
              </label>
            </div>
            {hasPassenger && (
              <div className="form-grid">
                <div className="form-row">
                  <div className="form-group">
                    <label>First name(s)</label>
                    <input type="text" maxLength={60} value={passenger.firstName} onChange={e => setPassenger({ ...passenger, firstName: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Surname</label>
                    <input type="text" maxLength={60} value={passenger.surname} onChange={e => setPassenger({ ...passenger, surname: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Address</label>
                    <input type="text" maxLength={120} value={passenger.address} onChange={e => setPassenger({ ...passenger, address: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Postcode</label>
                    <input type="text" maxLength={10} value={passenger.postcode} onChange={e => setPassenger({ ...passenger, postcode: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Date of birth</label>
                    <DobSelect value={passenger.dob} onChange={dob => setPassenger({ ...passenger, dob })} />
                    {passengerUnder18 && <span className="under18-note">Under 18 on event date — parent/guardian consent required below</span>}
                  </div>
                  <div className="form-group">
                    <label>Telephone</label>
                    <input type="tel" maxLength={25} value={passenger.tel} onChange={e => setPassenger({ ...passenger, tel: e.target.value })} onBlur={() => touch('passengerTel')} />
                    <FieldError show={touched.passengerTel || submitAttempted} message={fieldErrors.passengerTel} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>ACU Licence / Registration No.</label>
                    <input type="text" maxLength={30} value={passenger.acuLicence} onChange={e => setPassenger({ ...passenger, acuLicence: e.target.value })} onBlur={() => touch('passengerAcuLicence')} />
                    <FieldError show={touched.passengerAcuLicence || submitAttempted} message={fieldErrors.passengerAcuLicence} />
                  </div>
                  <div className="form-group">
                    <label>ACU Affiliated Club</label>
                    <input type="text" maxLength={60} value={passenger.acuClub} onChange={e => setPassenger({ ...passenger, acuClub: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Passenger&apos;s emergency contact (name &amp; number)</label>
                    <input type="text" maxLength={100} value={passengerEmergencyContact} onChange={e => setPassengerEmergencyContact(e.target.value)} onBlur={() => touch('passengerEmergencyContact')} />
                    <FieldError show={touched.passengerEmergencyContact || submitAttempted} message={fieldErrors.passengerEmergencyContact} />
                  </div>
                </div>
              </div>
            )}
            {!hasPassenger && <p className="muted-note no-print">No passenger on this entry.</p>}
          </div>

          {/* Entrant if different */}
          <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
            <div className="form-section-label" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              Entrant
              <label className="inline-checkbox no-print">
                <input type="checkbox" checked={entrantDiffers} onChange={e => setEntrantDiffers(e.target.checked)} />
                Different from rider (e.g. team/machine owner)
              </label>
            </div>
            {entrantDiffers ? (
              <div className="form-grid">
                <div className="form-row">
                  <div className="form-group">
                    <label>Company / Name</label>
                    <input type="text" maxLength={60} value={entrant.name} onChange={e => setEntrant({ ...entrant, name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Entrant&apos;s Licence No.</label>
                    <input type="text" maxLength={30} value={entrant.licenceNo} onChange={e => setEntrant({ ...entrant, licenceNo: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input type="text" maxLength={120} value={entrant.address} onChange={e => setEntrant({ ...entrant, address: e.target.value })} />
                </div>
              </div>
            ) : (
              <p className="muted-note no-print">Entrant is the rider.</p>
            )}
          </div>

          {/* Machines */}
          <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
            <div className="sec-section-header">
              <div className="form-section-label" style={{ marginBottom: 0 }}>Machine entries</div>
              <button type="button" className="sec-primary-btn no-print" onClick={addMachine} disabled={atMachineCap}>
                + Add another machine / class
              </button>
            </div>
            {atMachineCap && (
              <p className="muted-note no-print" style={{ marginTop: '-0.5rem', marginBottom: '0.75rem' }}>
                {licenceOption === 'giag'
                  ? 'Give It A Go covers one machine.'
                  : <>This event&apos;s pricing only covers one machine per entry — contact the secretary if you need to enter more than one.</>}
              </p>
            )}
            {machines.map((m, i) => (
              <div key={m.id} className="machine-block">
                <div className="machine-block-header">
                  <span className="form-section-label" style={{ margin: 0 }}>{machineLabel(m, i)}</span>
                  {machines.length > 1 && (
                    <button type="button" className="btn-delete no-print" onClick={() => removeMachine(m.id)}>Remove</button>
                  )}
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Class entered</label>
                    <div className="class-flags-choice">
                      {visibleClassFlags.map(f => (
                        <label key={f.key} className="inline-checkbox">
                          <input
                            type="checkbox"
                            checked={m.classFlags[f.key]}
                            onChange={() => toggleMachineClassFlag(m.id, f.key)}
                            onBlur={() => touch(`machine-${i}-classFlags`)}
                          />
                          {f.label}
                        </label>
                      ))}
                    </div>
                    <FieldError show={touched[`machine-${i}-classFlags`] || submitAttempted} message={machineFieldErrors[i]?.classFlags} />
                  </div>
                  <div className="form-row-3">
                    <div className="form-group">
                      <label>Riding No. preferred</label>
                      <input type="text" placeholder="eg 511" maxLength={10} value={m.ridingNo} onChange={e => updateMachine(m.id, 'ridingNo', e.target.value)} onBlur={() => touch(`machine-${i}-ridingNo`)} />
                      <FieldError show={touched[`machine-${i}-ridingNo`] || submitAttempted} message={machineFieldErrors[i]?.ridingNo} />
                      <p className="payment-info-note" style={{ marginTop: '0.5rem' }}>
                        If you do not already have a race number assigned to you from a previous event, make sure
                        to contact the secretary to obtain one <strong>before</strong> submitting this form.
                        Otherwise you may have to resubmit your entry.
                      </p>
                      {(() => {
                        const savedBike = getSavedBikeForRidingNo(m.ridingNo);
                        if (!savedBike) return null;
                        const matchedBike = getMatchedBike(m);
                        const key = m.ridingNo.trim();
                        return (
                          <span className="muted-note no-print">
                            {matchedBike && (
                              <>
                                Fill in your {[matchedBike.make, matchedBike.model].filter(Boolean).join(' ') || 'saved bike'} (No. {key})?{' '}
                                <button type="button" className="email-suggestion-btn" onClick={() => applyBikeSuggestion(m.id, matchedBike)}>
                                  Yes, fill in
                                </button>
                                {' · '}
                              </>
                            )}
                            <button type="button" className="email-suggestion-btn" onClick={() => forgetSavedBike(key)}>
                              Forget this bike
                            </button>
                          </span>
                        );
                      })()}
                    </div>
                    <div className="form-group">
                      <label>Machine model</label>
                      <input type="text" placeholder="eg CRF" maxLength={40} value={m.model} onChange={e => updateMachine(m.id, 'model', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Vehicle type</label>
                      <select value={m.vehicleType} onChange={e => updateMachine(m.id, 'vehicleType', e.target.value)} onBlur={() => touch(`machine-${i}-vehicleType`)}>
                        {VEHICLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <FieldError show={touched[`machine-${i}-vehicleType`] || submitAttempted} message={machineFieldErrors[i]?.vehicleType} />
                    </div>
                  </div>
                  <div className="form-row-3">
                    <div className="form-group">
                      <label>Make</label>
                      <input type="text" placeholder="eg Honda" maxLength={40} value={m.make} onChange={e => updateMachine(m.id, 'make', e.target.value)} onBlur={() => touch(`machine-${i}-make`)} />
                      <FieldError show={touched[`machine-${i}-make`] || submitAttempted} message={machineFieldErrors[i]?.make} />
                    </div>
                    <div className="form-group">
                      <label>Capacity (cc)</label>
                      <input type="text" placeholder="eg 450" maxLength={10} value={m.capacity} onChange={e => updateMachine(m.id, 'capacity', e.target.value)} onBlur={() => touch(`machine-${i}-capacity`)} />
                      <FieldError show={touched[`machine-${i}-capacity`] || submitAttempted} message={machineFieldErrors[i]?.capacity} />
                    </div>
                    <div className="form-group">
                      <label>Stroke (mm)</label>
                      <input type="text" placeholder="eg 60.83" maxLength={10} value={m.stroke} onChange={e => updateMachine(m.id, 'stroke', e.target.value)} onBlur={() => touch(`machine-${i}-stroke`)} />
                      <FieldError show={touched[`machine-${i}-stroke`] || submitAttempted} message={machineFieldErrors[i]?.stroke} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Payment */}
          <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
            <div className="form-section-label">Payment</div>
            <div className="form-grid">
              <div className="form-group">
                <label>Payment method</label>
                <div className="payment-method-choice">
                  <label className="inline-checkbox">
                    <input type="radio" name="paymethod" checked={payment.method === 'bacs'}
                      onChange={() => setPayment({ ...payment, method: 'bacs' })} />
                    Bank transfer (BACS)
                  </label>
                  <label className="inline-checkbox">
                    <input type="radio" name="paymethod" checked={payment.method === 'cheque'}
                      onChange={() => setPayment({ ...payment, method: 'cheque' })} />
                    Cheque (post with printed form)
                  </label>
                  <label className="inline-checkbox">
                    <input type="radio" name="paymethod" checked={payment.method === 'covered'}
                      onChange={() => setPayment({ ...payment, method: 'covered' })} />
                    I have a code
                  </label>
                </div>
              </div>
              <div className="form-row-4">
                <div className="form-group">
                  <label>Amount due</label>
                  <div className="currency-input-wrap">
                    <span className="currency-prefix">£</span>
                    <input
                      type="text"
                      value={amountDue && !amountDue.missingPrice ? amountDue.total.toFixed(2) : ''}
                      placeholder={!event ? 'Loading…' : amountDue?.missingPrice ? 'Contact secretary for amount' : ''}
                      disabled
                    />
                  </div>
                  {(licenceOption || dayMembership) && amountDue && !amountDue.missingPrice && (
                    <span className="muted-note">
                      {licenceOption === 'giag'
                        ? `Give It A Go £${amountDue.licenceFee.toFixed(2)} (includes the entry)`
                        : [`Entry fees £${amountDue.entryFees.toFixed(2)}`,
                           licenceOption === 'day_licence' && `day licence £${amountDue.licenceFee.toFixed(2)}`,
                           dayMembership && `day membership £${amountDue.membershipFee.toFixed(2)}`,
                          ].filter(Boolean).join(' + ')}
                    </span>
                  )}
                </div>
                {payment.method !== 'covered' && (
                  <div className="form-group">
                    <label>AMOUNT ({payment.method === 'cheque' ? 'Cheque' : 'Bank Transfer'})</label>
                    <div className="currency-input-wrap">
                      <span className="currency-prefix">£</span>
                      <input type="number" step="0.01" min="0" value={payment.amount}
                        onChange={e => setPayment({ ...payment, amount: e.target.value })}
                        onWheel={e => e.target.blur()}
                        onBlur={() => touch('paymentAmount')} />
                    </div>
                    <FieldError show={touched.paymentAmount || submitAttempted} message={fieldErrors.paymentAmount} />
                  </div>
                )}
                {payment.method === 'bacs' && (
                  <>
                    <div className="form-group">
                      <label>Bank transfer reference to use</label>
                      <input
                        type="text"
                        value={payment.bankReference}
                        placeholder={!payment.bankReference
                          ? (licenceOption ? "Enter the rider's surname above first" : "Enter Rider's ACU Licence number above first") : ''}
                        disabled
                      />
                    </div>
                    <div className="form-group">
                      <label className="inline-checkbox">
                        <input type="checkbox" checked={payment.coversOthers}
                          onChange={e => setPayment({ ...payment, coversOthers: e.target.checked })} />
                        Generate a code to share this payment
                      </label>
                      <span className="muted-note">
                        Optional — if this one bank transfer also covers other entrants, tick this to get a code
                        after submitting that you can share with them, so they can enter it instead of paying
                        themselves.
                      </span>
                    </div>
                  </>
                )}
                {payment.method === 'covered' && (
                  <div className="form-group">
                    <label>Coverage code</label>
                    <input type="text" placeholder="e.g. ABCD-1234-EF" maxLength={20} value={payment.coveredByCode}
                      onChange={e => setPayment({ ...payment, coveredByCode: e.target.value })}
                      onBlur={() => touch('paymentCoveredByCode')} />
                    <FieldError show={touched.paymentCoveredByCode || submitAttempted} message={fieldErrors.paymentCoveredByCode} />
                    <span className="muted-note">Enter the code the payer shared with you — no payment is needed from you.</span>
                  </div>
                )}
              </div>
            </div>
            {payment.method === 'bacs' && (
              <p className="payment-info-note" style={{ marginTop: '0.75rem' }}>
                You may pay by bank transfer (BACS) using your banking application.
                <br />
                Bank information: Payee <strong>&quot;NHCA&quot;</strong>, Sort code <strong>&quot;51-70-16&quot;</strong>, Account number{' '}
                <strong>&quot;79339883&quot;</strong>{payment.bankReference ? (
                  <>, payment reference <strong>&quot;{payment.bankReference}&quot;</strong></>
                ) : (
                  <>. Enter the Rider&apos;s ACU Licence number above to calculate your payment reference.</>
                )}
              </p>
            )}
            {payment.method === 'cheque' && (
              <p className="muted-note no-print" style={{ marginTop: '0.75rem' }}>
                Cheques can&apos;t be submitted online — after signing below, use &quot;Print entry form&quot; and post it with your
                cheque to the event secretary.
              </p>
            )}
            {payment.method === 'covered' && (
              <p className="payment-info-note" style={{ marginTop: '0.75rem' }}>
                Your entry fee is covered by someone else&apos;s bank transfer — you don&apos;t need to pay anything yourself.
              </p>
            )}
          </div>

          {/* Note to secretary */}
          <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
            <div className="form-section-label">Note to secretary <span className="req-tag">(optional)</span></div>
            <div className="form-group">
              <textarea
                rows={3}
                maxLength={500}
                placeholder="Anything else the secretary should know — eg running late, a request to be grouped with another rider, first event…"
                value={secretaryNote}
                onChange={e => setSecretaryNote(e.target.value)}
              />
            </div>
          </div>

          {/* Declarations & signature */}
          <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
            <div className="form-section-label">Declaration</div>
            <p style={{ fontSize: '0.85rem' }}>
              In consideration of being permitted to participate in this event I declare that I will be bound by the
              following, and I acknowledge the risks of motorsport set out below.
            </p>
            <ul className="declaration-list">
              {DECLARATIONS.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
            <p className="declaration-risk"><strong>Acknowledgement of the risks of motorsport:</strong> {RISK_ACKNOWLEDGEMENT}</p>

            <div className="form-grid" style={{ marginTop: '1rem' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Rider&apos;s typed signature (full name)</label>
                  <input type="text" maxLength={60} value={signature.riderName} onChange={e => setSignature({ ...signature, riderName: e.target.value })} onBlur={() => touch('signatureRiderName')} />
                  <FieldError show={touched.signatureRiderName || submitAttempted} message={fieldErrors.signatureRiderName} />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={signature.riderDate || todayIso()} onChange={e => setSignature({ ...signature, riderDate: e.target.value })} />
                </div>
              </div>
              {hasPassenger && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Passenger&apos;s typed signature (full name)</label>
                    <input type="text" maxLength={60} value={signature.passengerName} onChange={e => setSignature({ ...signature, passengerName: e.target.value })} onBlur={() => touch('signaturePassengerName')} />
                    <FieldError show={touched.signaturePassengerName || submitAttempted} message={fieldErrors.signaturePassengerName} />
                  </div>
                  <div className="form-group">
                    <label>Date</label>
                    <input type="date" value={signature.passengerDate || todayIso()} onChange={e => setSignature({ ...signature, passengerDate: e.target.value })} />
                  </div>
                </div>
              )}
              {needsParentGuardian && (
                <>
                  <p style={{ fontSize: '0.85rem' }}>
                    For riders and passengers under 18 years of age — I accept the above conditions of entry to this
                    event and give my approval.
                  </p>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Parent/guardian typed signature (required)</label>
                      <input type="text" maxLength={60} value={signature.parentGuardianName} onChange={e => setSignature({ ...signature, parentGuardianName: e.target.value })} onBlur={() => touch('signatureParentGuardianName')} />
                      <FieldError show={touched.signatureParentGuardianName || submitAttempted} message={fieldErrors.signatureParentGuardianName} />
                      <span className="muted-note">
                        Riders and Passengers under 18 years must also complete a &apos;Parental Agreement Form&apos; in addition
                        to this entry form. Parental Agreement Forms are available from the organisers and the ACU.
                        Their Parent or Legal Guardian must attend signing on with them and must be available for the
                        duration of the meeting.
                      </span>
                    </div>
                    <div className="form-group">
                      <label>Date</label>
                      <input type="date" value={signature.parentGuardianDate || todayIso()} onChange={e => setSignature({ ...signature, parentGuardianDate: e.target.value })} />
                    </div>
                  </div>
                </>
              )}
              <label className="inline-checkbox agree-checkbox">
                <input type="checkbox" checked={signature.agreed} onChange={e => setSignature({ ...signature, agreed: e.target.checked })} onBlur={() => touch('signatureAgreed')} />
                {hasPassenger || needsParentGuardian ? 'We have read and agree to the declarations above.' : 'I have read and agree to the declarations above.'}
              </label>
              <FieldError show={touched.signatureAgreed || submitAttempted} message={fieldErrors.signatureAgreed} />
            </div>
          </div>

          <label className="inline-checkbox no-print" style={{ marginTop: '0.75rem' }}>
            <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} />
            Remember my details on this device for next time (don&apos;t use on a shared computer)
          </label>
          <p className="muted-note no-print" style={{ marginTop: '0.25rem' }}>
            Saved only in your browser — never sent anywhere unless you submit an entry. Not your payment details or signature.
          </p>

          <div className="form-actions no-print">
            <button type="submit" className="sec-primary-btn" disabled={submitting || submitted}>
              {submitting ? 'Submitting…' : submitted ? '✅ Entry submitted' : 'Review / Submit entry'}
            </button>
            <button type="button" className="sec-secondary-btn" onClick={handlePrint} disabled={submitting}>
              {submitting ? 'Submitting…' : '🖨️ Print entry form'}
            </button>
          </div>
          {submitted && (
            <p className="muted-note no-print" style={{ marginTop: '0.25rem' }}>
              Your entry has been sent, so the form can&apos;t be submitted again from this page.
              To change something, contact the secretary.
            </p>
          )}
        </form>
        </>
        )}
      </main>

      <footer className="App-footer no-print">
        <p>NHCA Online Entry | National Hillclimb Association</p>
      </footer>
    </div>
  );
}
